import React, { useState, useEffect } from "react";
import axios from "axios";
import ApiUrl from "../Environment/ApiUrl";
import {
  DollarSign,
  ShoppingBag,
  Receipt,
  TrendingUp,
  Wallet,
  Package,
} from "lucide-react";

const Dashboard = () => {
  // State variables
  const [selectedDate, setSelectedDate] = useState();
  const [selectedBranch, setSelectedBranch] = useState("Main Branch");
  const [salesData, setSalesData] = useState([]);
  const [purchasesData, setPurchasesData] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [purchasesError, setPurchasesError] = useState(null);
  const [stocksData, setStocksData] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [stocksError, setStocksError] = useState(null);
  const [cardData, setCardData] = useState({});
  const [stocked, setstockdata] = useState(0); // Start as number

  const [activeTable, setActiveTable] = useState("sales");
  const [chartData, setChartData] = useState([]);
  const [maxValue, setMaxValue] = useState(10000);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [pieData, setPieData] = useState([]);

  // Tooltip style
  const tooltipStyle = {
    position: "absolute",
    left: `${tooltipPos.x}px`,
    top: `${tooltipPos.y}px`,
    backgroundColor: "white",
    padding: "5px 10px",
    borderRadius: "4px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    border: "1px solid #ddd",
    pointerEvents: "none",
    zIndex: 100,
    fontSize: "12px",
    fontWeight: "bold",
  };

  // Colors for pie chart segments
  const pieColors = [
    "#FF6384",
    "#36A2EB",
    "#FFCE56",
    "#4BC0C0",
    "#9966FF",
    "#FF9F40",
    "#8AC24A",
    "#F06292",
    "#7986CB",
    "#FF7043",
  ];

  // Set today's date on initial load
  useEffect(() => {
    const now = new Date();
    const localISOTime = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 16); // "YYYY-MM-DDTHH:MM"
    setSelectedDate(localISOTime);
  }, []);

  // Retrieve branch from localStorage
  useEffect(() => {
    const branchFromStorage = localStorage.getItem("branch_Name");
    if (branchFromStorage) {
      setSelectedBranch(branchFromStorage);
    }
  }, []);

  // Process data for pie chart based on product types
  const processPieChartData = (stockList) => {
    const categoryCounts = {};

    // Count occurrences of each category
    stockList.forEach((stock) => {
      const category = stock.category || "Unknown";
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    // Sort categories by count descending
    const sortedCategories = Object.keys(categoryCounts).sort(
      (a, b) => categoryCounts[b] - categoryCounts[a]
    );

    // Map to pie chart data (no filter, show all categories)
    const processedData = sortedCategories.map((category, index) => ({
      label: category,
      value: categoryCounts[category],
      color: pieColors[index % pieColors.length],
    }));

    setPieData(processedData);
  };

  useEffect(() => {
    const fetchStock = async () => {
      try {
        const response = await axios.get(`${ApiUrl}/stock_data.php`);
        const stockArray = response.data.data; // This is the actual array of products

        // Calculate total available_quantity
        const totalAvailableQuantity = stockArray.reduce((total, item) => {
          return total + Number(item.available_quantity);
        }, 0);

        setstockdata(totalAvailableQuantity); // Save just the array part in state
        console.log("Total available_quantity:", totalAvailableQuantity);
      } catch (error) {
        console.error("Error fetching stock data:", error);
      }
    };

    fetchStock();
  }, []);

  // Fetch sales data
  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setSalesLoading(true);
        const response = await axios.get(`${ApiUrl}/sales_data.php`);
        if (response.data && response.data.data) {
          setSalesData(response.data.data);
        } else {
          setSalesData([]);
        }
      } catch (error) {
        console.error("Error fetching sales data:", error);
        setSalesError("Failed to load sales data.");
        setSalesData([]);
      } finally {
        setSalesLoading(false);
      }
    };
    fetchSalesData();
  }, []);

  // Fetch purchase data
  useEffect(() => {
    const fetchPurchasesData = async () => {
      try {
        setPurchasesLoading(true);
        const response = await axios.get(`${ApiUrl}/purchase_data.php`);
        if (response.data && response.data.data) {
          setPurchasesData(response.data.data);
        } else {
          setPurchasesData([]);
        }
      } catch (error) {
        console.error("Error fetching purchase data:", error);
        setPurchasesError("Failed to load purchase data.");
        setPurchasesData([]);
      } finally {
        setPurchasesLoading(false);
      }
    };
    fetchPurchasesData();
  }, []);

  // Fetch card data
  useEffect(() => {
    const fetchCardData = async () => {
      try {
        const response = await axios.get(`${ApiUrl}/dashboard_card.php`);
        if (response.data && response.data?.data) {
          setCardData(response.data);
        } else {
          setCardData([]);
        }
      } catch (error) {
        console.log("Error fetching card data:", error);
        setCardData([]);
      }
    };
    fetchCardData();
  }, []);

  // Fetch stock data
  useEffect(() => {
    const fetchStocksData = async () => {
      try {
        setStocksLoading(true);
        const response = await axios.get(`${ApiUrl}/stock_data.php`);
        if (response.data && response.data.data) {
          setStocksData(response.data.data);
        } else {
          setStocksData([]);
        }
      } catch (error) {
        console.error("Error fetching stock data:", error);
        setStocksError("Failed to load stock data.");
        setStocksData([]);
      } finally {
        setStocksLoading(false);
      }
    };
    fetchStocksData();
  }, []);

  // Process pie chart data when stocksData changes
  useEffect(() => {
    if (stocksData.length > 0) {
      processPieChartData(stocksData);
    }
  }, [stocksData]);

  // Process chart data for the last 6 months
  useEffect(() => {
    const processChartData = () => {
      const today = new Date();
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        months.push({
          month: date.toLocaleString("default", { month: "short" }),
          year: date.getFullYear(),
          purchase: 0,
          sales: 0,
        });
      }

      // Aggregate sales data
      salesData.forEach((sale) => {
        const saleDate = new Date(sale.Purchase_Date);
        const monthYear = `${saleDate.toLocaleString("default", {
          month: "short",
        })} ${saleDate.getFullYear()}`;
        const monthIndex = months.findIndex(
          (m) => `${m.month} ${m.year}` === monthYear
        );
        if (monthIndex !== -1) {
          months[monthIndex].sales += parseFloat(sale.Net_total || 0);
        }
      });

      // Aggregate purchase data
      purchasesData.forEach((purchase) => {
        const purchaseDate = new Date(purchase.purchaseDate);
        const monthYear = `${purchaseDate.toLocaleString("default", {
          month: "short",
        })} ${purchaseDate.getFullYear()}`;
        const monthIndex = months.findIndex(
          (m) => `${m.month} ${m.year}` === monthYear
        );
        if (monthIndex !== -1) {
          months[monthIndex].purchase += parseFloat(purchase.netTotal || 0);
        }
      });

      // Set chart data and max value
      setChartData(months);
      const max = Math.max(
        ...months.map((d) => Math.max(d.purchase, d.sales)),
        10000 // Default minimum max value
      );
      setMaxValue(Math.ceil(max / 1000) * 1000);
    };

    if (salesData.length || purchasesData.length) {
      processChartData();
    }
  }, [salesData, purchasesData]);

  // Filter sales data for selected date
  const getSalesForSelectedDate = () => {
    if (!selectedDate || !salesData.length) return [];
    return salesData.filter(
      (sale) => sale.Purchase_Date?.split(" ")[0] === selectedDate
    );
  };

  // Filter purchases data for selected date
  const getPurchasesForSelectedDate = () => {
    if (!selectedDate || !purchasesData.length) return [];
    return purchasesData.filter(
      (purchase) => purchase.purchaseDate?.split(" ")[0] === selectedDate
    );
  };

  // Get low stock items
  const getLowStockItems = () => {
    if (!stocksData.length) return [];
    return stocksData.filter((stock) => {
      const availableQty =
        parseInt(stock.total_purchase_quantity) - parseInt(stock.sold_quantity);
      return availableQty < 5;
    });
  };

  return (
    <div className="p-2" style={{ overflowX: "hidden", width: "100%" }}>
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <h2 className="m-0 fs-5 fw-semibold text-dark">Dashboard</h2>
        <div className="d-flex gap-3 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 fw-semibold text-muted">
              Date:
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ minWidth: "150px" }}
              disabled
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row mb-3">
        <div className="col-lg-4 col-md-6">
          <div className="bg-white  rounded-3 p-3  card shadow-sm border border-light">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="text-muted text-uppercase small fw-bold mb-2">
                  Customers
                </div>
                <div className="fs-6 fw-bold text-dark mb-1">
                  {cardData?.data?.customers || 0}
                </div>
              </div>
              <div
                className="bg-primary rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: "30px", height: "30px" }}
              >
                <i className="bi bi-people text-white fs-5"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6">
          <div className="bg-white rounded-3 card p-3 shadow-sm border border-light">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="text-muted text-uppercase small fw-bold mb-2">
                  Dealers
                </div>
                <div className="fs-6 fw-bold text-dark mb-1">
                  {cardData?.data?.dealers || 0}
                </div>
              </div>
              <div
                className="bg-primary rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: "30px", height: "30px" }}
              >
                <i className="bi bi-building text-white fs-5"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6">
          <div className="bg-white rounded-3  card p-3 shadow-sm border border-light">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="text-muted text-uppercase small fw-bold mb-2">
                  Stocks
                </div>
                <div className="fs-6 fw-bold text-dark mb-1">
                  {stocked || 0}
                </div>
              </div>
              <div
                className="bg-primary rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: "30px", height: "30px" }}
              >
                <i className="bi bi-boxes text-white fs-5"></i>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="col-lg-3 col-md-6">
          <div className="bg-white rounded-3 p-3 shadow-sm border border-light">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="text-muted text-uppercase small fw-bold mb-2">?</div>
                <div className="fs-6 fw-bold text-dark mb-1">{cardData?.data?.sales || 0}</div>
              </div>
              <div className="bg-primary rounded-3 d-flex align-items-center justify-content-center" style={{ width: "30px", height: "30px" }}>
                <i className="bi bi-cart-check text-white fs-5"></i>
              </div>
            </div>
          </div>
        </div> */}
      </div>

      {/* Charts Row */}
      <div className="row mb-3">
        {/* Bar Chart */}
        <div className="col-lg-8">
          <div className="bg-white rounded-3 p-3 shadow-sm card border border-light">
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <h6 className="card-title fw-bold">Monthly Sales Analysis</h6>
              <div className="d-flex gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "2px",
                      backgroundColor: "#00aeffff",
                    }}
                  ></div>
                  <span className="small">Purchase</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "2px",
                      backgroundColor: "#76db17ff",
                    }}
                  ></div>
                  <span className="small">Sales</span>
                </div>
              </div>
            </div>
            <div className="position-relative" style={{ height: "300px" }}>
              {/* Tooltip */}
              {hoveredBar && (
                <div style={tooltipStyle}>
                  {hoveredBar.month} {hoveredBar.year}
                  <br />
                  {hoveredBar.type === "purchase" ? "Purchase" : "Sales"}: ₹
                  {hoveredBar.value.toLocaleString()}
                </div>
              )}

              <svg
                width="100%"
                height="100%"
                viewBox="0 0 700 350"
                style={{ maxWidth: "100%" }}
                onMouseLeave={() => setHoveredBar(null)}
              >
                {/* Grid lines */}
                {[0, 0.2, 0.4, 0.6, 0.8, 1].map((fraction, i) => (
                  <line
                    key={`grid-${i}`}
                    x1="70"
                    y1={320 - fraction * 250}
                    x2="650"
                    y2={320 - fraction * 250}
                    stroke="#e9ecef"
                    strokeWidth="1"
                  />
                ))}
                {/* Y-axis labels */}
                {[0, 0.2, 0.4, 0.6, 0.8, 1].map((fraction, i) => (
                  <text
                    key={`y-label-${i}`}
                    x="60"
                    y={325 - fraction * 250}
                    fontSize="12"
                    fill="#6c757d"
                    textAnchor="end"
                  >
                    {Math.round(fraction * maxValue).toLocaleString()}
                  </text>
                ))}
                {/* Bars */}
                {chartData.map((data, i) => {
                  const barWidth = 25;
                  const spacing =
                    chartData.length > 1 ? 580 / (chartData.length - 1) : 0;
                  const x = 100 + i * spacing;
                  const purchaseHeight = (data.purchase / maxValue) * 250;
                  const salesHeight = (data.sales / maxValue) * 250;

                  return (
                    <g key={`bars-${i}`}>
                      {/* Purchase Bar */}
                      <rect
                        x={x}
                        y={320 - purchaseHeight}
                        width={barWidth}
                        height={purchaseHeight}
                        fill="#00aeffff"
                        rx="3"
                        onMouseEnter={(e) => {
                          const svgRect = e.target.getBoundingClientRect();
                          setTooltipPos({
                            x: svgRect.left + window.scrollX,
                            y: svgRect.top + window.scrollY - 40,
                          });
                          setHoveredBar({
                            type: "purchase",
                            value: data.purchase,
                            month: data.month,
                            year: data.year,
                          });
                        }}
                        onMouseMove={(e) => {
                          const svgRect = e.target.getBoundingClientRect();
                          setTooltipPos({
                            x: svgRect.left + window.scrollX,
                            y: svgRect.top + window.scrollY - 40,
                          });
                        }}
                      />
                      <text
                        x={x + barWidth / 2}
                        y={310 - purchaseHeight}
                        fontSize="10"
                        fill="#00aeffff"
                        textAnchor="middle"
                      >
                        {Math.round(data.purchase).toLocaleString()}
                      </text>

                      {/* Sales Bar */}
                      <rect
                        x={x + 30}
                        y={320 - salesHeight}
                        width={barWidth}
                        height={salesHeight}
                        fill="#76db17ff"
                        rx="3"
                        onMouseEnter={(e) => {
                          const svgRect = e.target.getBoundingClientRect();
                          setTooltipPos({
                            x: svgRect.left + window.scrollX,
                            y: svgRect.top + window.scrollY - 40,
                          });
                          setHoveredBar({
                            type: "sales",
                            value: data.sales,
                            month: data.month,
                            year: data.year,
                          });
                        }}
                        onMouseMove={(e) => {
                          const svgRect = e.target.getBoundingClientRect();
                          setTooltipPos({
                            x: svgRect.left + window.scrollX,
                            y: svgRect.top + window.scrollY - 40,
                          });
                        }}
                      />
                      <text
                        x={x + 30 + barWidth / 2}
                        y={310 - salesHeight}
                        fontSize="10"
                        fill="#76db17ff"
                        textAnchor="middle"
                      >
                        {Math.round(data.sales).toLocaleString()}
                      </text>
                    </g>
                  );
                })}
                {/* X-axis labels */}
                {chartData.map((data, i) => (
                  <text
                    key={`x-label-${i}`}
                    x={
                      100 +
                      i *
                        (chartData.length > 1
                          ? 580 / (chartData.length - 1)
                          : 0) +
                      30
                    }
                    y="340"
                    fontSize="14"
                    fill="#495057"
                    textAnchor="middle"
                    fontWeight="500"
                  >
                    {data.month}
                  </text>
                ))}
              </svg>
            </div>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="col-lg-4">
          <div className="bg-white rounded-3 p-3 shadow-sm card border border-light">
            <h6 className="card-title fw-bold">Product Type Distribution</h6>
            <div className="d-flex flex-column align-items-center">
              <div className="position-relative">
                <svg width="220" height="225" viewBox="0 0 220 220">
                  <circle
                    cx="110"
                    cy="110"
                    r="90"
                    fill="#f8f9fa"
                    stroke="#e9ecef"
                    strokeWidth="2"
                  />
                  {(() => {
                    let currentAngle = 0;
                    const total = pieData.reduce(
                      (sum, item) => sum + item.value,
                      0
                    );
                    return pieData.map((item, i) => {
                      const angle = (item.value / total) * 360;
                      const startAngle = currentAngle;
                      const endAngle = currentAngle + angle;
                      const x1 =
                        110 + 90 * Math.cos((startAngle * Math.PI) / 180);
                      const y1 =
                        110 + 90 * Math.sin((startAngle * Math.PI) / 180);
                      const x2 =
                        110 + 90 * Math.cos((endAngle * Math.PI) / 180);
                      const y2 =
                        110 + 90 * Math.sin((endAngle * Math.PI) / 180);
                      const largeArcFlag = angle > 180 ? 1 : 0;
                      const pathData = [
                        `M 110 110`,
                        `L ${x1} ${y1}`,
                        `A 90 90 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                        "Z",
                      ].join(" ");
                      currentAngle += angle;
                      return (
                        <path
                          key={`pie-${i}`}
                          d={pathData}
                          fill={item.color}
                          stroke="white"
                          strokeWidth="2"
                        />
                      );
                    });
                  })()}
                  <circle
                    cx="110"
                    cy="110"
                    r="35"
                    fill="white"
                    stroke="#dee2e6"
                    strokeWidth="2"
                  />
                  <text
                    x="110"
                    y="110"
                    textAnchor="middle"
                    fontSize="16"
                    fontWeight="bold"
                    fill="#495057"
                  >
                    {pieData.reduce((sum, item) => sum + item.value, 0)}
                  </text>
                  <text
                    x="110"
                    y="130"
                    textAnchor="middle"
                    fontSize="12"
                    fill="#6c757d"
                  >
                    Products
                  </text>
                </svg>
              </div>
              <div className="w-100 mt-3">
                {pieData.map((item, i) => (
                  <div
                    key={`legend-${i}`}
                    className="d-flex justify-content-between align-items-center py-2 border-bottom border-light"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          backgroundColor: item.color,
                          width: "14px",
                          height: "14px",
                          borderRadius: "3px",
                        }}
                      ></div>
                      <span className="text-muted small">{item.label}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark small">
                        {item.value}
                      </span>
                      <span className="text-muted small">
                        (
                        {Math.round(
                          (item.value /
                            pieData.reduce(
                              (sum, item) => sum + item.value,
                              0
                            )) *
                            100
                        )}
                        %)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-3 shadow-sm card border border-light mb-5">
        <div className="p-4 border-bottom border-light d-flex justify-content-between flex-wrap gap-2">
          <div>
            <span className="mb-0 fw-bold">
              {activeTable === "sales"
                ? "Today's Sales"
                : activeTable === "purchases"
                ? "Today's Purchases"
                : "Stock Inventory"}
              {activeTable !== "stocks" && ` (${selectedDate})`}
            </span>
          </div>
          <div className="d-flex gap-2">
            <button
              className={`btn btn-sm ${
                activeTable === "sales" ? "btn-success" : "btn-outline-success"
              }`}
              onClick={() => setActiveTable("sales")}
            >
              Sales
            </button>
            <button
              className={`btn btn-sm ${
                activeTable === "purchases"
                  ? "btn-success"
                  : "btn-outline-success"
              }`}
              onClick={() => setActiveTable("purchases")}
            >
              Purchase
            </button>
            <button
              className={`btn btn-sm ${
                activeTable === "stocks" ? "btn-success" : "btn-outline-success"
              }`}
              onClick={() => setActiveTable("stocks")}
            >
              Stock
            </button>
          </div>
        </div>
        <div className="table-responsive">
          {activeTable === "sales" && (
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>S.No</th>
                  <th>Sales ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Total Amount</th>
                  <th>Paid Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {salesLoading ? (
                  <tr>
                    <td colSpan="7" className="text-center p-5">
                      Loading sales data...
                    </td>
                  </tr>
                ) : salesError ? (
                  <tr>
                    <td colSpan="7" className="text-center p-5 text-danger">
                      {salesError}
                    </td>
                  </tr>
                ) : getSalesForSelectedDate().length > 0 ? (
                  getSalesForSelectedDate().map((sale, index) => (
                    <tr key={sale.Sales_ID || index}>
                      <td>{index + 1}</td>
                      <td>
                        <span className="text-primary fw-semibold">
                          {sale.Sales_ID}
                        </span>
                      </td>
                      <td>{sale.Purchase_Date}</td>
                      <td>{sale.Customer_ID}</td>
                      <td>₹{Number(sale.Net_total).toLocaleString()}</td>
                      <td>₹{Number(sale.Paid_Amount).toLocaleString()}</td>
                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 ${
                            sale.Payment_States === "Paid"
                              ? "bg-success"
                              : sale.Payment_States === "Partial"
                              ? "bg-warning"
                              : "bg-danger"
                          }`}
                        >
                          {sale.Payment_States}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center p-5">
                      No sales found for {selectedDate}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {activeTable === "purchases" && (
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>S.No</th>
                  <th>Purchase ID</th>
                  <th>Date</th>
                  <th>Dealer Name</th>
                  <th>Total Amount</th>
                  <th>Paid Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {purchasesLoading ? (
                  <tr>
                    <td colSpan="7" className="text-center p-5">
                      Loading purchase data...
                    </td>
                  </tr>
                ) : purchasesError ? (
                  <tr>
                    <td colSpan="7" className="text-center p-5 text-danger">
                      {purchasesError}
                    </td>
                  </tr>
                ) : getPurchasesForSelectedDate().length > 0 ? (
                  getPurchasesForSelectedDate().map((purchase, index) => (
                    <tr key={purchase.id || index}>
                      <td>{index + 1}</td>
                      <td>
                        <span className="text-primary fw-semibold">
                          {purchase.purchaseId}
                        </span>
                      </td>
                      <td>{purchase.purchaseDate}</td>
                      <td>{purchase.dealerName}</td>
                      <td>₹{Number(purchase.netTotal).toLocaleString()}</td>
                      <td>₹{Number(purchase.paidAmount).toLocaleString()}</td>
                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 ${
                            purchase.paymentStatus === "Paid"
                              ? "bg-success"
                              : purchase.paymentStatus === "Partial"
                              ? "bg-warning"
                              : "bg-danger"
                          }`}
                        >
                          {purchase.paymentStatus}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center p-5">
                      No purchases found for {selectedDate}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {activeTable === "stocks" && (
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>S.No</th>

                  <th>HSN Code</th>
                  <th>Category</th>

                  <th>Type</th>
                  <th>Tyre Size</th>
                  <th>Sold Qty</th>
                  <th>Purchase Qty</th>
                  <th>Available Qty</th>
                </tr>
              </thead>
              <tbody>
                {stocksLoading ? (
                  <tr>
                    <td colSpan="10" className="text-center p-5">
                      Loading stock data...
                    </td>
                  </tr>
                ) : stocksError ? (
                  <tr>
                    <td colSpan="10" className="text-center p-5 text-danger">
                      {stocksError}
                    </td>
                  </tr>
                ) : stocksData.length > 0 ? (
                  stocksData.map((stock, index) => {
                    const availableQty =
                      parseInt(stock.total_purchase_quantity) -
                      parseInt(stock.sold_quantity);
                    return (
                      <tr key={stock.id || index}>
                        <td>{index + 1}</td>

                        <td>{stock.hsn_code}</td>
                        <td>{stock.category}</td>

                        <td>{stock.type}</td>
                        <td>{stock.tyre_size}</td>
                        <td>{stock.sold_quantity}</td>
                        <td>{stock.total_purchase_quantity}</td>
                        <td
                          className={
                            availableQty < 5 ? "fw-bold text-danger" : ""
                          }
                        >
                          {availableQty}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center p-5">
                      No stock data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
